<template>
  <section
    class="private-org-admin"
    :style="{ backgroundColor }"
    :aria-labelledby="sectionTitleId"
  >
    <header class="private-org-admin__header">
      <div>
        <h1 :id="sectionTitleId">{{ sectionTitle }}</h1>
        <p>
          {{
            section === "members"
              ? "Membership and role assignment data comes from the private organisation authority."
              : "Roles and permissions are validated by the server before every change."
          }}
        </p>
      </div>
      <UnitButton
        v-if="section === 'roles' && canManageRoles"
        size="small"
        @click="beginCreateRole"
      >
        Create role
      </UnitButton>
      <UnitButton
        v-if="section === 'members' && canInviteMembers && !inviteOpen"
        ref="inviteTrigger"
        size="small"
        data-invite-members
        @click="beginInvite"
      >
        Invite members
      </UnitButton>
    </header>

    <p
      v-if="!commandsEnabled && !loading && !errorMessage"
      class="private-org-admin__notice"
      data-admin-commands-disabled
    >
      {{
        section === "members"
          ? "Inviting, removing members and changing role assignments are not enabled yet."
          : "Creating, editing and deleting roles are not enabled yet."
      }}
    </p>

    <LoadingState
      v-if="loading && members.length === 0"
      variant="section"
      message="Loading organisation administration…"
    />

    <div
      v-else-if="errorMessage"
      class="private-org-admin__state"
      role="alert"
    >
      <p>{{ errorMessage }}</p>
      <UnitButton variant="secondary" size="small" @click="reload">
        Retry
      </UnitButton>
    </div>

    <template v-else-if="section === 'members'">
      <section
        v-if="inviteOpen"
        class="private-org-admin__invite"
        aria-labelledby="private-org-invite-title"
        data-invite-panel
      >
        <h2 id="private-org-invite-title" ref="inviteHeading" tabindex="-1">
          Invite members
        </h2>
        <template v-if="inviteResult">
          <p data-invite-expiry-summary>
            This link can be accepted once. Share it only with the person you
            want to invite. It expires on
            <time :datetime="inviteResult.expiresAtIso">{{ inviteResult.expiresAtLabel }}</time>.
          </p>
          <p class="private-org-admin__invite-link" data-invite-link>
            {{ inviteResult.link }}
          </p>
          <div class="private-org-admin__form-actions">
            <span
              class="private-org-admin__copy-feedback"
              role="status"
              aria-live="polite"
              data-invite-copy-status
            >
              {{ copyMessage }}
            </span>
            <UnitButton
              variant="secondary"
              size="small"
              data-invite-copy
              @click="copyInviteLink"
            >
              <template #leading>
                <svg class="compactImg" aria-hidden="true">
                  <use href="@/assets/icons/iconset.svg#copy_compact"></use>
                </svg>
              </template>
              Copy link
            </UnitButton>
            <UnitButton variant="secondary" size="small" @click="beginInvite">
              Create another link
            </UnitButton>
            <UnitButton size="small" @click="closeInvite">Done</UnitButton>
          </div>
        </template>
        <form v-else class="private-org-admin__invite-form" @submit.prevent="createInvite">
          <p>
            Create a single-use link. The person who accepts it joins this
            organisation with the roles you choose.
          </p>
          <fieldset v-if="roles.length" :disabled="busyKey === 'invite'">
            <legend>Roles to assign on joining (optional)</legend>
            <label
              v-for="role in roles"
              :key="role.roleId"
              class="private-org-admin__checkbox"
            >
              <input
                v-model="inviteDraft.roleIds"
                type="checkbox"
                :value="role.roleId"
                :disabled="
                  !inviteDraft.roleIds.includes(role.roleId) &&
                  inviteDraft.roleIds.length >= maxInviteRoles
                "
              />
              {{ role.title }}
            </label>
            <small v-if="roles.length > maxInviteRoles">
              Choose up to {{ maxInviteRoles }} roles.
            </small>
          </fieldset>
          <label>
            Link expires after
            <select
              ref="inviteExpiryField"
              v-model="inviteDraft.expiry"
              :disabled="busyKey === 'invite'"
              data-invite-expiry
            >
              <option
                v-for="option in inviteExpiryOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <p
            v-if="inviteError"
            class="private-org-admin__invite-error"
            role="alert"
            data-invite-error
          >
            {{ inviteError }}
          </p>
          <div class="private-org-admin__form-actions">
            <UnitButton
              variant="secondary"
              size="small"
              :disabled="busyKey === 'invite'"
              @click="closeInvite"
            >
              Cancel
            </UnitButton>
            <UnitButton
              type="submit"
              size="small"
              :loading="busyKey === 'invite'"
              loading-label="Creating invite link"
              data-invite-submit
            >
              Create invite link
            </UnitButton>
          </div>
        </form>
      </section>

      <p v-if="members.length === 0" class="private-org-admin__state">
        No active members are available.
      </p>
      <ul v-else class="private-org-admin__list" aria-label="Organisation members">
        <li
          v-for="member in members"
          :key="member.membershipRef"
          class="private-org-admin__member"
        >
          <div class="private-org-admin__identity">
            <span class="private-org-admin__avatar" aria-hidden="true">
              {{ initials(member.displayName) }}
            </span>
            <div>
              <div class="private-org-admin__name">
                {{ member.displayName }}
                <Badge v-if="member.isSelf" message="You" tone="neutral" />
              </div>
              <small>@{{ member.publicIdentifier }}</small>
            </div>
          </div>

          <div class="private-org-admin__assignments">
            <div
              v-for="assignment in member.assignments"
              :key="assignment.assignmentRef"
              class="private-org-admin__assignment"
            >
              <Badge
                :message="roleTitle(assignment.roleId)"
                :tone="assignment.status === 'pending-acceptance' ? 'warning' : 'neutral'"
              />
              <small v-if="assignment.status === 'pending-acceptance'">
                Awaiting disclosure acceptance
              </small>
              <UnitButton
                v-if="canManageRoles"
                variant="text"
                size="micro"
                :disabled="busyKey === member.membershipRef"
                @click="unassignRole(member, assignment)"
              >
                Remove role
              </UnitButton>
            </div>
            <div
              v-if="canManageRoles && availableRoles(member).length"
              class="private-org-admin__assign-control"
            >
              <label :for="`role-for-${member.membershipRef}`">Assign role</label>
              <select
                :id="`role-for-${member.membershipRef}`"
                v-model="selectedRoles[member.membershipRef]"
                :disabled="busyKey === member.membershipRef"
              >
                <option value="">Choose a role</option>
                <option
                  v-for="role in availableRoles(member)"
                  :key="role.roleId"
                  :value="role.roleId"
                >
                  {{ role.title }}
                </option>
              </select>
              <UnitButton
                variant="secondary"
                size="micro"
                :disabled="
                  !selectedRoles[member.membershipRef] ||
                  busyKey === member.membershipRef
                "
                @click="assignRole(member)"
              >
                Assign
              </UnitButton>
            </div>
          </div>

          <div
            v-if="!member.isSelf && (canManageMembers || canDeleteAccounts)"
            class="private-org-admin__member-actions"
          >
            <UnitButton
              v-if="canManageMembers"
              variant="danger"
              size="micro"
              :disabled="busyKey === member.membershipRef"
              @click="memberPendingRemoval = member"
            >
              Remove member
            </UnitButton>
            <UnitButton
              v-if="canDeleteAccounts"
              variant="danger"
              size="micro"
              :disabled="busyKey === member.membershipRef"
              :aria-label="`Delete account of ${member.displayName}`"
              data-delete-member-account
              @click="accountDeletionTarget = member"
            >
              Delete account…
            </UnitButton>
          </div>
        </li>
      </ul>
      <UnitButton
        v-if="hasMore"
        variant="secondary"
        size="small"
        :loading="loading"
        loading-label="Loading members"
        @click="loadMore"
      >
        Load more
      </UnitButton>
    </template>

    <template v-else>
      <form
        v-if="roleDraft && canManageRoles"
        class="private-org-admin__role-form"
        @submit.prevent="saveRole"
      >
        <h2>{{ editingRoleId ? "Edit role" : "Create role" }}</h2>
        <label>
          Title
          <input v-model.trim="roleDraft.title" maxlength="100" required />
        </label>
        <label>
          Description
          <textarea v-model.trim="roleDraft.description" maxlength="500" />
        </label>
        <div class="private-org-admin__form-grid">
          <label>
            Definition visibility
            <select v-model="roleDraft.definitionVisibility">
              <option value="public">Public definition</option>
              <option value="members">Members</option>
              <option value="administrators">Administrators</option>
            </select>
          </label>
          <label>
            Holder disclosure
            <select v-model="roleDraft.holderDisclosurePolicy">
              <option value="transparent">Transparent</option>
              <option value="member-choice">Member choice</option>
              <option value="confidential">Confidential</option>
            </select>
          </label>
          <label>
            Maximum holders
            <input
              v-model.number="roleDraft.maxHolders"
              type="number"
              min="1"
              max="25"
              required
            />
          </label>
        </div>
        <fieldset>
          <legend>Capabilities</legend>
          <label
            v-for="capability in capabilityOptions"
            :key="capability.key"
            class="private-org-admin__checkbox"
          >
            <input
              v-model="roleDraft.capabilities[capability.key]"
              type="checkbox"
            />
            {{ capability.label }}
          </label>
        </fieldset>
        <div class="private-org-admin__form-actions">
          <UnitButton
            variant="secondary"
            :disabled="busyKey === 'role-form'"
            @click="cancelRoleEdit"
          >
            Cancel
          </UnitButton>
          <UnitButton
            type="submit"
            :loading="busyKey === 'role-form'"
            loading-label="Saving role"
          >
            Save role
          </UnitButton>
        </div>
      </form>

      <p v-if="roles.length === 0" class="private-org-admin__state">
        No active roles are available.
      </p>
      <ul v-else class="private-org-admin__list" aria-label="Organisation roles">
        <li v-for="role in roles" :key="role.roleId" class="private-org-admin__role">
          <div>
            <div class="private-org-admin__name">
              {{ role.title }}
              <Badge :message="`${role.holderCount}/${role.maxHolders} holders`" />
            </div>
            <p>{{ role.description || "No description" }}</p>
            <small>
              {{ role.definitionVisibility }} · {{ role.holderDisclosurePolicy }}
            </small>
          </div>
          <div v-if="canManageRoles" class="private-org-admin__role-actions">
            <UnitButton variant="text" size="micro" @click="beginEditRole(role)">
              Edit
            </UnitButton>
            <UnitButton
              variant="danger"
              size="micro"
              :disabled="busyKey === role.roleId"
              @click="rolePendingDeletion = role"
            >
              Delete
            </UnitButton>
          </div>
        </li>
      </ul>
    </template>

    <p class="private-org-admin__announcer" aria-live="polite">
      {{ statusMessage }}
    </p>

    <ConfirmDialog
      :visible="Boolean(memberPendingRemoval)"
      title="Remove member?"
      :message="
        memberPendingRemoval
          ? `${memberPendingRemoval.displayName} will lose access to this organisation. Their personal items remain theirs.`
          : ''
      "
      tone="danger"
      confirm-label="Remove member"
      :busy="busyKey === memberPendingRemoval?.membershipRef"
      @close="memberPendingRemoval = null"
      @confirm="removeMember"
    />

    <AccountDeletionReview
      v-if="canDeleteAccounts"
      :visible="Boolean(accountDeletionTarget)"
      mode="admin"
      :target="
        accountDeletionTarget
          ? { orgId, membershipRef: accountDeletionTarget.membershipRef }
          : null
      "
      @close="accountDeletionTarget = null"
      @deleted="handleAccountDeleted"
    />

    <ConfirmDialog
      :visible="Boolean(rolePendingDeletion)"
      title="Delete role?"
      :message="
        rolePendingDeletion
          ? `${rolePendingDeletion.title} will be removed from its holders. This cannot orphan the organisation's final administrator.`
          : ''
      "
      tone="danger"
      confirm-label="Delete role"
      :busy="busyKey === rolePendingDeletion?.roleId"
      @close="rolePendingDeletion = null"
      @confirm="deleteRole"
    />
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";

import AccountDeletionReview from "@/components/AccountComponents/AccountDeletionReview.vue";
import Badge from "@/components/GenericComponents/Badge.vue";
import ConfirmDialog from "@/components/GenericComponents/ConfirmDialog.vue";
import LoadingState from "@/components/GenericComponents/LoadingState.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import {
  isAccountAdministrationFeatureEnabled,
  isOrganisationAuthorityCommandsFeatureEnabled,
} from "@/config/runtimeConfig";
import { organisationContextService } from "@/services/organisationContextService";
import { currentUserIsSuperAdmin } from "@/services/superAdminAccess";
import {
  createOrganisationCommandOperationId,
  organisationCommandAuthorityService,
} from "@/services/organisationCommandAuthorityService";
import { copyTextToClipboard } from "@/utils/clipboard";

const props = defineProps({
  orgId: { type: String, required: true },
  section: {
    type: String,
    required: true,
    validator: (value) => ["members", "roles"].includes(value),
  },
  backgroundColor: { type: String, default: "transparent" },
  // Public handle used as the cosmetic first segment of invite links. The
  // accept route resolves the organisation from the opaque token only.
  organisationPublicIdentifier: { type: String, default: "" },
});

// Mutations are served by organisation-authority command callables. Until the
// command gate is on, the panel is read-only so admins never see controls that
// can only fail with `feature-disabled`.
const commandsEnabled = isOrganisationAuthorityCommandsFeatureEnabled === true;

const MAX_INVITE_ROLES = 10;
const DAY_MS = 24 * 60 * 60 * 1000;
// Server bounds: INVITE_TTL_MINUTES (15 min) to INVITE_TTL_MAX_DAYS (30 days),
// checked against server time. Keep a small margin under the maximum so a
// browser clock running slightly ahead is not rejected.
const INVITE_MAX_TTL_MS = 30 * DAY_MS;
const INVITE_CLOCK_MARGIN_MS = 5 * 60 * 1000;
const inviteExpiryOptions = Object.freeze([
  { key: "1h", label: "1 hour", durationMs: 60 * 60 * 1000 },
  { key: "1d", label: "1 day", durationMs: DAY_MS },
  { key: "7d", label: "7 days", durationMs: 7 * DAY_MS },
  { key: "14d", label: "14 days", durationMs: 14 * DAY_MS },
  { key: "30d", label: "30 days", durationMs: INVITE_MAX_TTL_MS },
]);
const DEFAULT_INVITE_EXPIRY = "7d";
const maxInviteRoles = MAX_INVITE_ROLES;

const capabilityOptions = Object.freeze([
  { key: "organisationAdministration", label: "Manage organisation" },
  { key: "membershipAdministration", label: "Manage members" },
  { key: "roleAdministration", label: "Manage roles" },
  { key: "appearanceAdministration", label: "Manage appearance" },
  { key: "moderateItemPlacements", label: "Moderate item placements" },
  { key: "walletAdministration", label: "Manage Wallet" },
  { key: "monetaryAdministration", label: "Manage monetary policy" },
  {
    key: "siblingRelationshipAdministration",
    label: "Manage organisation relationships",
  },
  { key: "apiKeyAdministration", label: "Manage API keys" },
]);

const members = ref([]);
const roles = ref([]);
const capabilities = reactive({ canManageMembers: false, canManageRoles: false });
const nextCursor = ref(null);
const hasMore = ref(false);
const loading = ref(false);
const errorMessage = ref("");
const statusMessage = ref("");
const busyKey = ref("");
const selectedRoles = reactive({});
const memberPendingRemoval = ref(null);
const rolePendingDeletion = ref(null);
const accountDeletionTarget = ref(null);
const canDeleteAccounts = ref(false);
const roleDraft = ref(null);
const editingRoleId = ref(null);
const editingRoleRevision = ref(null);
const organisationRevision = ref(null);
const inviteOpen = ref(false);
const inviteDraft = ref(null);
const inviteResult = ref(null);
const inviteError = ref("");
const copyMessage = ref("");
const inviteTrigger = ref(null);
const inviteHeading = ref(null);
const inviteExpiryField = ref(null);
let loadGeneration = 0;

const canManageMembers = computed(
  () => commandsEnabled && capabilities.canManageMembers,
);
const canManageRoles = computed(
  () => commandsEnabled && capabilities.canManageRoles,
);
const canInviteMembers = computed(
  () =>
    canManageMembers.value &&
    Number.isSafeInteger(organisationRevision.value),
);

const sectionTitleId = computed(() => `private-org-${props.section}-title`);
const sectionTitle = computed(() =>
  props.section === "members" ? "Members" : "Roles",
);

const emptyCapabilities = () =>
  Object.fromEntries(capabilityOptions.map(({ key }) => [key, false]));

const describeError = (error) => {
  const messages = {
    aborted: "This organisation changed. Reload the latest version and try again.",
    "failed-precondition": "The organisation authority is being reconciled.",
    "permission-denied": "You no longer have access to this administration panel.",
    unavailable: "Organisation administration is temporarily unavailable.",
    "feature-disabled":
      "This organisation administration feature is not enabled yet. Nothing was changed.",
  };
  return messages[error?.code] || "Organisation administration could not be loaded.";
};

const loadPage = async ({ reset = false } = {}) => {
  if (loading.value) return;
  const generation = ++loadGeneration;
  const requestedOrgId = props.orgId;
  const cursor = reset ? null : nextCursor.value;
  loading.value = true;
  errorMessage.value = "";
  try {
    const response = await organisationContextService.getOrganisationAdministration({
      orgId: requestedOrgId,
      cursor,
    });
    if (generation !== loadGeneration || requestedOrgId !== props.orgId) return;
    if (reset) members.value = response.members;
    else members.value = [...members.value, ...response.members];
    roles.value = response.roles;
    capabilities.canManageMembers = response.capabilities.canManageMembers;
    capabilities.canManageRoles = response.capabilities.canManageRoles;
    organisationRevision.value = response.organisationRevision;
    nextCursor.value = response.nextCursor;
    hasMore.value = response.hasMore;
  } catch (error) {
    if (generation !== loadGeneration || requestedOrgId !== props.orgId) return;
    errorMessage.value = describeError(error);
  } finally {
    if (generation === loadGeneration) loading.value = false;
  }
};

const reload = () => {
  nextCursor.value = null;
  hasMore.value = false;
  return loadPage({ reset: true });
};
const loadMore = () => loadPage();
const roleTitle = (roleId) =>
  roles.value.find((role) => role.roleId === roleId)?.title || "Unavailable role";
const availableRoles = (member) => {
  const assigned = new Set(member.assignments.map(({ roleId }) => roleId));
  return roles.value.filter(
    (role) => !assigned.has(role.roleId) && role.holderCount < role.maxHolders,
  );
};
const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "U";

const runMemberCommand = async (member, action) => {
  busyKey.value = member.membershipRef;
  errorMessage.value = "";
  try {
    await action();
    statusMessage.value = "Organisation membership updated.";
    await reload();
    return true;
  } catch (error) {
    errorMessage.value = describeError(error);
    return false;
  } finally {
    busyKey.value = "";
  }
};

const assignRole = async (member) => {
  const roleId = selectedRoles[member.membershipRef];
  if (!roleId) return;
  await runMemberCommand(member, () =>
    organisationCommandAuthorityService.assignRole({
      operationId: createOrganisationCommandOperationId("assign_role"),
      expectedRevision: member.revision,
      orgId: props.orgId,
      membershipRef: member.membershipRef,
      roleId,
    }),
  );
  selectedRoles[member.membershipRef] = "";
};

const unassignRole = (member, assignment) =>
  runMemberCommand(member, () =>
    organisationCommandAuthorityService.removeRoleAssignment({
      operationId: createOrganisationCommandOperationId("remove_role"),
      expectedRevision: member.revision,
      orgId: props.orgId,
      membershipRef: member.membershipRef,
      roleId: assignment.roleId,
    }),
  );

const removeMember = async () => {
  const member = memberPendingRemoval.value;
  if (!member) return;
  const succeeded = await runMemberCommand(member, () =>
    organisationCommandAuthorityService.removeMember({
      operationId: createOrganisationCommandOperationId("remove_member"),
      expectedRevision: member.revision,
      orgId: props.orgId,
      membershipRef: member.membershipRef,
    }),
  );
  if (succeeded) memberPendingRemoval.value = null;
};

const beginCreateRole = () => {
  editingRoleId.value = null;
  editingRoleRevision.value = null;
  roleDraft.value = {
    title: "",
    description: "",
    definitionVisibility: "members",
    holderDisclosurePolicy: "member-choice",
    maxHolders: 5,
    capabilities: emptyCapabilities(),
  };
};

const beginEditRole = (role) => {
  editingRoleId.value = role.roleId;
  editingRoleRevision.value = role.revision;
  roleDraft.value = {
    title: role.title,
    description: role.description || "",
    definitionVisibility: role.definitionVisibility,
    holderDisclosurePolicy: role.holderDisclosurePolicy,
    maxHolders: role.maxHolders,
    capabilities: { ...role.capabilities },
  };
};

const cancelRoleEdit = () => {
  roleDraft.value = null;
  editingRoleId.value = null;
  editingRoleRevision.value = null;
};

const saveRole = async () => {
  if (!roleDraft.value || busyKey.value) return;
  busyKey.value = "role-form";
  errorMessage.value = "";
  const role = {
    ...roleDraft.value,
    description: roleDraft.value.description || null,
    maxHolders: Number(roleDraft.value.maxHolders),
    capabilities: { ...roleDraft.value.capabilities },
  };
  try {
    if (editingRoleId.value) {
      await organisationCommandAuthorityService.updateRole({
        operationId: createOrganisationCommandOperationId("update_role"),
        expectedRevision: editingRoleRevision.value,
        orgId: props.orgId,
        roleId: editingRoleId.value,
        role,
      });
    } else {
      await organisationCommandAuthorityService.createRole({
        operationId: createOrganisationCommandOperationId("create_role"),
        expectedRevision: 0,
        orgId: props.orgId,
        role,
      });
    }
    statusMessage.value = editingRoleId.value ? "Role updated." : "Role created.";
    cancelRoleEdit();
    await reload();
  } catch (error) {
    errorMessage.value = describeError(error);
  } finally {
    busyKey.value = "";
  }
};

const deleteRole = async () => {
  const role = rolePendingDeletion.value;
  if (!role || busyKey.value) return;
  busyKey.value = role.roleId;
  errorMessage.value = "";
  try {
    await organisationCommandAuthorityService.deleteRole({
      operationId: createOrganisationCommandOperationId("delete_role"),
      expectedRevision: role.revision,
      orgId: props.orgId,
      roleId: role.roleId,
    });
    rolePendingDeletion.value = null;
    statusMessage.value = "Role deleted.";
    await reload();
  } catch (error) {
    errorMessage.value = describeError(error);
  } finally {
    busyKey.value = "";
  }
};

const describeInviteError = (error) => {
  const messages = {
    aborted:
      "This organisation changed while the link was being created. Try again.",
    "not-found": "A selected role is no longer available. Choose again.",
    "invalid-argument":
      "The invite settings were not accepted. Check the roles and expiry.",
  };
  return messages[error?.code] || describeError(error);
};

const formatExpiry = (millis) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(millis));

const buildInviteLink = (inviteToken) => {
  // Route: /invite/:orgUID/:inviteUID (router/index.js). AcceptInviteView
  // previews and accepts using the token alone and ignores :orgUID, so the
  // public identifier is used there instead of the internal orgId.
  const organisationSegment =
    props.organisationPublicIdentifier || props.orgId;
  return `${window.location.origin}/invite/${encodeURIComponent(
    organisationSegment,
  )}/${encodeURIComponent(inviteToken)}`;
};

const beginInvite = async () => {
  if (!canInviteMembers.value) return;
  inviteDraft.value = { roleIds: [], expiry: DEFAULT_INVITE_EXPIRY };
  inviteResult.value = null;
  inviteError.value = "";
  copyMessage.value = "";
  inviteOpen.value = true;
  await nextTick();
  inviteExpiryField.value?.focus();
};

const closeInvite = async () => {
  if (busyKey.value === "invite") return;
  inviteOpen.value = false;
  inviteDraft.value = null;
  inviteResult.value = null;
  inviteError.value = "";
  copyMessage.value = "";
  await nextTick();
  inviteTrigger.value?.$el?.focus?.();
};

const createInvite = async () => {
  if (!inviteDraft.value || busyKey.value || !canInviteMembers.value) return;
  const option =
    inviteExpiryOptions.find(({ key }) => key === inviteDraft.value.expiry) ||
    inviteExpiryOptions.find(({ key }) => key === DEFAULT_INVITE_EXPIRY);
  const now = Date.now();
  const expiresAt =
    now +
    Math.min(option.durationMs, INVITE_MAX_TTL_MS - INVITE_CLOCK_MARGIN_MS);
  const activeRoleIds = new Set(roles.value.map(({ roleId }) => roleId));
  const roleIds = inviteDraft.value.roleIds
    .filter((roleId) => activeRoleIds.has(roleId))
    .slice(0, MAX_INVITE_ROLES);
  const requestedOrgId = props.orgId;

  busyKey.value = "invite";
  inviteError.value = "";
  try {
    const result = await organisationCommandAuthorityService.createInvite({
      operationId: createOrganisationCommandOperationId("create_invite"),
      expectedRevision: organisationRevision.value,
      orgId: requestedOrgId,
      roleIds,
      expiresAt,
    });
    if (requestedOrgId !== props.orgId || !inviteOpen.value) return;
    inviteResult.value = {
      link: buildInviteLink(result.inviteToken),
      expiresAtIso: new Date(result.expiresAt).toISOString(),
      expiresAtLabel: formatExpiry(result.expiresAt),
    };
    inviteDraft.value = null;
    statusMessage.value = "Invite link created.";
    await nextTick();
    inviteHeading.value?.focus();
  } catch (error) {
    if (requestedOrgId !== props.orgId) return;
    inviteError.value = describeInviteError(error);
    if (error?.code === "aborted") reload();
  } finally {
    busyKey.value = "";
  }
};

const copyInviteLink = async () => {
  if (!inviteResult.value) return;
  try {
    await copyTextToClipboard(inviteResult.value.link);
    copyMessage.value = "Invite link copied.";
  } catch (_error) {
    copyMessage.value = "Copy failed. Select the link above and copy it manually.";
  }
};

watch(
  () => props.orgId,
  () => {
    loadGeneration += 1;
    members.value = [];
    roles.value = [];
    capabilities.canManageMembers = false;
    capabilities.canManageRoles = false;
    organisationRevision.value = null;
    inviteOpen.value = false;
    inviteDraft.value = null;
    inviteResult.value = null;
    inviteError.value = "";
    copyMessage.value = "";
    errorMessage.value = "";
    statusMessage.value = "";
    accountDeletionTarget.value = null;
    loading.value = false;
    reload();
  },
);
const handleAccountDeleted = async ({ current = true } = {}) => {
  if (current) accountDeletionTarget.value = null;
  statusMessage.value = "Account deleted.";
  await reload();
};

const resolveAccountAdministration = async () => {
  if (!isAccountAdministrationFeatureEnabled) return;
  canDeleteAccounts.value = await currentUserIsSuperAdmin();
};

onMounted(() => {
  reload();
  resolveAccountAdministration();
});
</script>

<style scoped>
.private-org-admin {
  display: grid;
  gap: var(--space-5);
  padding: var(--space-5);
  border-radius: var(--radius-md);
}

.private-org-admin__header,
.private-org-admin__member,
.private-org-admin__role,
.private-org-admin__form-actions,
.private-org-admin__assign-control,
.private-org-admin__role-actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.private-org-admin__header,
.private-org-admin__member,
.private-org-admin__role {
  flex-wrap: wrap;
  justify-content: space-between;
}

.private-org-admin__assign-control {
  flex-wrap: wrap;
}

.private-org-admin h1,
.private-org-admin h2,
.private-org-admin p {
  margin: 0;
}

.private-org-admin__header p,
.private-org-admin__role p,
.private-org-admin small {
  color: var(--color-text-tertiary);
}

.private-org-admin__list {
  display: grid;
  gap: var(--space-3);
  padding: 0;
  margin: 0;
  list-style: none;
}

.private-org-admin__member,
.private-org-admin__role {
  padding: var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-subtle);
}

.private-org-admin__identity,
.private-org-admin__name,
.private-org-admin__assignment {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.private-org-admin__avatar {
  display: inline-grid;
  width: var(--control-height-compact);
  border-radius: 50%;
  background: var(--color-surface-muted);
  font-weight: var(--font-weight-semibold);
  place-items: center;
  aspect-ratio: 1;
}

.private-org-admin__assignments {
  display: grid;
  flex: 1 1 320px;
  gap: var(--space-2);
}

.private-org-admin__assign-control select,
.private-org-admin__role-form input,
.private-org-admin__role-form textarea,
.private-org-admin__role-form select {
  min-height: var(--control-height-compact);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-canvas);
  color: var(--color-text-primary);
  font: inherit;
}

.private-org-admin__role-form,
.private-org-admin__role-form label,
.private-org-admin__role-form fieldset {
  display: grid;
  gap: var(--space-2);
}

.private-org-admin__role-form {
  gap: var(--space-4);
  padding: var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

.private-org-admin__form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-3);
}

.private-org-admin__role-form label.private-org-admin__checkbox {
  display: flex;
  grid-template-columns: auto 1fr;
  align-items: center;
}

.private-org-admin__form-actions,
.private-org-admin__role-actions {
  justify-content: flex-end;
}

.private-org-admin__member-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.private-org-admin__notice {
  color: var(--color-text-tertiary);
}

.private-org-admin__invite,
.private-org-admin__invite-form,
.private-org-admin__invite-form label,
.private-org-admin__invite-form fieldset {
  display: grid;
  gap: var(--space-2);
}

.private-org-admin__invite {
  gap: var(--space-4);
  padding: var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

.private-org-admin__invite-form {
  gap: var(--space-4);
}

.private-org-admin__invite-form fieldset {
  margin: 0;
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

.private-org-admin__invite-form label.private-org-admin__checkbox {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.private-org-admin__invite-form select {
  min-height: var(--control-height-compact);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-field);
  color: var(--color-text-primary);
  font: inherit;
}

.private-org-admin__invite h2:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.private-org-admin__invite-link {
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-field);
  font-family: var(--font-family-mono);
  overflow-wrap: anywhere;
  user-select: all;
}

.private-org-admin__invite-error {
  color: var(--color-feedback-danger-foreground);
}

.private-org-admin__copy-feedback {
  margin-inline-end: auto;
  color: var(--color-text-tertiary);
}

.private-org-admin__announcer {
  min-height: 1em;
  color: var(--color-text-tertiary);
}

</style>
